// Shared presentational building blocks of the acceptance form (#70). Extracted so both the editable
// form (FabricationOrderAcceptanceForm) and the read-only history detail
// (FabricationOrderAcceptanceFormReadOnly) render the exact same sections. This is a leaf module
// (no dependency on the form or the history dialog) to keep the import graph acyclic.
import {
  Box, Button, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material'

// Sections sit on a subtly-shaded surface layered on top of the panel's Paper background — a theme
// token instead of a hardcoded color, so it adapts to light/dark mode.
export const sectionSx = {
  border: 1,
  borderColor: 'divider',
  backgroundColor: 'action.hover',
  borderRadius: 2,
  p: 1.5,
  mb: 1.5
}

// Sections 2 & 3 — a checklist of Passed / Failed / Remarks rows. `disabled` renders it read-only
// (used as-is by the history detail, where onChange is a no-op).
export function ChecklistTable ({ title, items, rows, onChange, disabled, t_ }) {
  return (
    <Box sx={sectionSx}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>{title}</Typography>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: 'text.secondary', width: '55%' }}>{t_('Document / Test')}</TableCell>
            <TableCell sx={{ color: 'text.secondary' }} align='center'>{t_('Passed')}</TableCell>
            <TableCell sx={{ color: 'text.secondary' }} align='center'>{t_('Failed')}</TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>{t_('Remarks')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow key={item.code}>
              <TableCell>{`${item.code} ${t_(item.label)}`}</TableCell>
              <TableCell align='center'>
                <Checkbox
                  size='small'
                  checked={rows[index].passed}
                  disabled={disabled}
                  inputProps={{ 'aria-label': `${t_('Passed')} ${item.code}` }}
                  onChange={(e) => onChange(index, 'passed', e.target.checked)}
                />
              </TableCell>
              <TableCell align='center'>
                <Checkbox
                  size='small'
                  checked={rows[index].failed}
                  disabled={disabled}
                  inputProps={{ 'aria-label': `${t_('Failed')} ${item.code}` }}
                  onChange={(e) => onChange(index, 'failed', e.target.checked)}
                />
              </TableCell>
              <TableCell>
                <TextField
                  variant='standard'
                  fullWidth
                  disabled={disabled}
                  value={rows[index].remarks}
                  placeholder={t_('Remarks')}
                  inputProps={{ 'aria-label': `${t_('Remarks')} ${item.code}` }}
                  onChange={(e) => onChange(index, 'remarks', e.target.value)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

// Section 4 — one Rank / Name / Structure / Date row of the acceptance committee (authority leader,
// member, contractor representative), plus its own handwritten-signature placeholder (the signature
// itself is collected on the printed, manually signed PDF — one line per person). `disabled` renders
// it read-only and hides the Remove action.
export function CommitteeRow ({ label, value, onChange, onRemove, disabled, signatureTestId, t_ }) {
  return (
    <TableRow>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {label}
        {onRemove && !disabled && (
          <Button size='small' color='error' onClick={onRemove} sx={{ ml: 1, minWidth: 0 }}>
            {t_('Remove')}
          </Button>
        )}
      </TableCell>
      <TableCell>
        <TextField
          variant='standard' fullWidth size='small' disabled={disabled}
          value={value.rank}
          inputProps={{ 'aria-label': `${t_('Rank')} ${label}` }}
          onChange={(e) => onChange('rank', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          variant='standard' fullWidth size='small' disabled={disabled}
          value={value.name}
          inputProps={{ 'aria-label': `${t_('Name')} ${label}` }}
          onChange={(e) => onChange('name', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          variant='standard' fullWidth size='small' disabled={disabled}
          value={value.structure}
          inputProps={{ 'aria-label': `${t_('Structure')} ${label}` }}
          onChange={(e) => onChange('structure', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <TextField
          variant='standard' type='date' fullWidth size='small' disabled={disabled}
          InputLabelProps={{ shrink: true }}
          value={value.date}
          inputProps={{ 'aria-label': `${t_('Date')} ${label}` }}
          onChange={(e) => onChange('date', e.target.value)}
        />
      </TableCell>
      <TableCell>
        <Box data-testid={signatureTestId} sx={{ minWidth: 100 }}>
          <Box sx={{ height: 20, borderBottom: 1, borderColor: 'divider' }} />
          <Typography sx={{ fontSize: 9, color: 'text.secondary', textTransform: 'uppercase', mt: 0.5, textAlign: 'center' }}>
            {t_('handwritten on paper')}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  )
}
